const menus = document.querySelectorAll('[data-updated-main-menu]');
const panelTransitionDuration = 220;
const backdropTransitionDuration = 220;
const megaAccordionTransitionDuration = 200;
const hoverOpenDelay = 90;
const mobileMegaBottomGap = 32;
// Use the media queries exported from Sass so CSS and menu behavior stay aligned.
const breakpointStyles = window.getComputedStyle(document.documentElement);
const menuMediaQuery = (property, fallback) => {
    const query = breakpointStyles.getPropertyValue(property).trim();

    if (!query) {
        console.warn(`Main menu: missing CSS property ${property}; using ${fallback}.`);
    }

    return window.matchMedia(query || fallback);
};
// Emergency defaults for missing styles; keep aligned with the Sass down queries.
const megaAccordionMediaQuery = menuMediaQuery('--breakpoint-mobile-down', '(max-width: 47.99875em)');
const primaryNavHiddenMediaQuery = menuMediaQuery('--breakpoint-tablet-down', '(max-width: 70.99875em)');
const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
].join(',');

menus.forEach((menu) => {
    const primaryNav = menu.querySelector('.updatedMainMenu-primaryNav');
    const menuHeader = menu.querySelector('.updatedMainMenu-inner');
    const triggers = menu.querySelectorAll('[data-updated-main-menu-trigger]');
    const hoverTriggers = menu.querySelectorAll('[data-updated-main-menu-hover]');
    const panels = menu.querySelectorAll('[data-updated-main-menu-panel]');
    const accordionTriggers = menu.querySelectorAll('[data-updated-main-menu-accordion-trigger]');
    const searchSuggestionTriggers = menu.querySelectorAll('[data-updated-main-menu-search-suggestion]');
    const backdrop = menu.querySelector('[data-updated-main-menu-backdrop]');
    const accordionCloseTimers = new WeakMap();
    const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const closeTimers = new WeakMap();
    let hoverOpenTimer = null;
    let hoverCloseTimer = null;
    let pointerInsideMenuTimer = null;
    let pointerStartedInsideMenu = false;
    let backdropCloseTimer = null;
    let bodyScrollLock = null;
    let activeTrigger = null;
    let activePanel = null;
    let activeOpenSource = null;

    const lockPageScroll = () => {
        if (bodyScrollLock) {
            return;
        }

        const scrollY = window.scrollY || document.documentElement.scrollTop || 0;

        bodyScrollLock = {
            left: document.body.style.left,
            position: document.body.style.position,
            right: document.body.style.right,
            scrollY,
            top: document.body.style.top,
            width: document.body.style.width
        };

        document.body.style.left = '0';
        document.body.style.position = 'fixed';
        document.body.style.right = '0';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.width = '100%';
    };

    const unlockPageScroll = () => {
        if (!bodyScrollLock) {
            return;
        }

        const { left, position, right, scrollY, top, width } = bodyScrollLock;

        document.body.style.left = left;
        document.body.style.position = position;
        document.body.style.right = right;
        document.body.style.top = top;
        document.body.style.width = width;
        bodyScrollLock = null;
        window.scrollTo(0, scrollY);
    };

    const isMobileMegaPanel = (panel) => Boolean(
        panel &&
        panel.classList.contains('updatedMainMenu-panel--mega') &&
        megaAccordionMediaQuery.matches
    );

    const isMobileMegaPanelOpen = () => Boolean(activePanel && activeTrigger && isMobileMegaPanel(activePanel));

    const setMobileMegaDialogState = (isDialog) => {
        const megaPanel = menu.querySelector('.updatedMainMenu-panel--mega');

        if (!megaPanel) {
            return;
        }

        if (isDialog) {
            megaPanel.setAttribute('role', 'dialog');
            megaPanel.setAttribute('aria-modal', 'true');
            return;
        }

        megaPanel.removeAttribute('role');
        megaPanel.removeAttribute('aria-modal');
    };

    const updateMobileMegaLayout = () => {
        const layerTop = Math.max(0, menuHeader.getBoundingClientRect().bottom);
        const panelHeight = Math.max(0, window.innerHeight - layerTop - mobileMegaBottomGap);

        menu.style.setProperty('--updated-main-menu-mobile-layer-top', `${layerTop}px`);
        menu.style.setProperty('--updated-main-menu-mobile-panel-height', `${panelHeight}px`);
    };

    const resetMobileMegaLayout = () => {
        menu.style.removeProperty('--updated-main-menu-mobile-layer-top');
        menu.style.removeProperty('--updated-main-menu-mobile-panel-height');
    };

    const syncMobileMegaState = () => {
        if (isMobileMegaPanelOpen()) {
            updateMobileMegaLayout();
            setMobileMegaDialogState(true);
            lockPageScroll();
            openBackdrop();
            return;
        }

        setMobileMegaDialogState(false);
        unlockPageScroll();
        closeBackdrop();
    };

    const openBackdrop = () => {
        if (!backdrop) {
            return;
        }

        window.clearTimeout(backdropCloseTimer);
        backdrop.hidden = false;

        window.requestAnimationFrame(() => {
            backdrop.classList.add('is-open');
        });
    };

    const closeBackdrop = () => {
        if (!backdrop) {
            return;
        }

        window.clearTimeout(backdropCloseTimer);
        backdrop.classList.remove('is-open');

        backdropCloseTimer = window.setTimeout(() => {
            if (!backdrop.classList.contains('is-open')) {
                backdrop.hidden = true;
                resetMobileMegaLayout();
            }
        }, backdropTransitionDuration);
    };

    const closePanels = (focusTrigger = false) => {
        const previousTrigger = activeTrigger || menu.querySelector('[data-updated-main-menu-trigger][aria-expanded="true"]');

        window.clearTimeout(hoverOpenTimer);
        setMobileMegaDialogState(false);
        unlockPageScroll();
        closeBackdrop();

        triggers.forEach((trigger) => {
            trigger.setAttribute('aria-expanded', 'false');
            trigger.classList.remove('is-open');
        });

        panels.forEach((panel) => {
            window.clearTimeout(closeTimers.get(panel));
            panel.classList.remove('is-open');

            const closeTimer = window.setTimeout(() => {
                if (!panel.classList.contains('is-open')) {
                    panel.hidden = true;
                }
            }, panelTransitionDuration);

            closeTimers.set(panel, closeTimer);
        });

        menu.classList.remove('is-open');
        activeTrigger = null;
        activePanel = null;
        activeOpenSource = null;

        if (focusTrigger && previousTrigger) {
            previousTrigger.focus();
        }
    };

    const openPanel = (trigger, panel, source = 'click') => {
        closePanels(false);
        window.clearTimeout(closeTimers.get(panel));

        trigger.setAttribute('aria-expanded', 'true');
        trigger.classList.add('is-open');
        panel.hidden = false;
        menu.classList.add('is-open');
        activeTrigger = trigger;
        activePanel = panel;
        activeOpenSource = source;

        window.requestAnimationFrame(() => {
            panel.classList.add('is-open');
        });

        const focusSelector = trigger.getAttribute('data-updated-main-menu-focus');
        const focusTarget = focusSelector ? menu.querySelector(focusSelector) : null;

        if (focusTarget) {
            window.requestAnimationFrame(() => focusTarget.focus());
        }

        syncMobileMegaState();
    };

    const keepPanelOpen = (trigger, panel, source = 'click') => {
        window.clearTimeout(closeTimers.get(panel));

        trigger.setAttribute('aria-expanded', 'true');
        trigger.classList.add('is-open');
        panel.hidden = false;
        panel.classList.add('is-open');
        menu.classList.add('is-open');
        activeTrigger = trigger;
        activePanel = panel;
        activeOpenSource = source;

        syncMobileMegaState();
    };

    const closeAccordion = (trigger, panel) => {
        window.clearTimeout(accordionCloseTimers.get(panel));
        trigger.setAttribute('aria-expanded', 'false');
        panel.classList.remove('is-open');

        if (panel.contains(document.activeElement)) {
            trigger.focus();
        }

        const closeTimer = window.setTimeout(() => {
            if (!panel.classList.contains('is-open') && megaAccordionMediaQuery.matches) {
                panel.hidden = true;
            }
        }, megaAccordionTransitionDuration);

        accordionCloseTimers.set(panel, closeTimer);
    };

    const openAccordion = (trigger, panel) => {
        window.clearTimeout(accordionCloseTimers.get(panel));
        trigger.setAttribute('aria-expanded', 'true');
        panel.hidden = false;

        window.requestAnimationFrame(() => {
            panel.classList.add('is-open');
        });
    };

    const setMegaAccordionMode = () => {
        if (!accordionTriggers.length) {
            return;
        }

        if (megaAccordionMediaQuery.matches) {
            menu.classList.add('has-mega-accordions');

            accordionTriggers.forEach((trigger) => {
                const panel = menu.querySelector(`#${trigger.getAttribute('aria-controls')}`);

                trigger.setAttribute('aria-expanded', 'false');

                if (panel) {
                    window.clearTimeout(accordionCloseTimers.get(panel));
                    panel.classList.remove('is-open');
                    panel.hidden = true;
                }
            });

            return;
        }

        menu.classList.remove('has-mega-accordions');

        accordionTriggers.forEach((trigger) => {
            const panel = menu.querySelector(`#${trigger.getAttribute('aria-controls')}`);

            trigger.setAttribute('aria-expanded', 'true');

            if (panel) {
                window.clearTimeout(accordionCloseTimers.get(panel));
                panel.hidden = false;
                panel.classList.add('is-open');
            }
        });
    };

    const closePrimaryNavPanelWhenHidden = () => {
        if (!primaryNavHiddenMediaQuery.matches || !primaryNav) {
            return;
        }

        const openPrimaryTrigger = primaryNav.querySelector('[data-updated-main-menu-trigger][aria-expanded="true"]');

        if (openPrimaryTrigger) {
            closePanels(false);
        }
    };

    const getFocusableElements = (root) => Array.from(root.querySelectorAll(focusableSelector))
        .filter((element) => element.tabIndex >= 0 && Boolean(element.offsetParent || element.getClientRects().length));

    const isHeaderTrigger = (trigger) => Boolean(menuHeader && menuHeader.contains(trigger));

    const getNextHeaderFocusTarget = (trigger) => {
        const headerFocusables = getFocusableElements(menuHeader || menu);
        const triggerIndex = headerFocusables.indexOf(trigger);

        return triggerIndex === -1 ? null : headerFocusables[triggerIndex + 1] || null;
    };

    const focusNextHeaderTarget = () => {
        const nextTarget = getNextHeaderFocusTarget(activeTrigger);

        if (!nextTarget) {
            closePanels(false);
            return false;
        }

        closePanels(false);
        nextTarget.focus();
        return true;
    };

    const handleHeaderDropdownTab = (event) => {
        if (
            event.key !== 'Tab' ||
            !activeTrigger ||
            !activePanel ||
            !isHeaderTrigger(activeTrigger)
        ) {
            return false;
        }

        const focusablePanelElements = getFocusableElements(activePanel);
        const firstPanelElement = focusablePanelElements[0];
        const lastPanelElement = focusablePanelElements[focusablePanelElements.length - 1];

        if (document.activeElement === activeTrigger && !event.shiftKey) {
            if (!firstPanelElement) {
                if (focusNextHeaderTarget()) {
                    event.preventDefault();
                }

                return true;
            }

            event.preventDefault();
            firstPanelElement.focus();
            return true;
        }

        if (document.activeElement === firstPanelElement && event.shiftKey) {
            event.preventDefault();
            activeTrigger.focus();
            return true;
        }

        if (document.activeElement === lastPanelElement && !event.shiftKey) {
            if (focusNextHeaderTarget()) {
                event.preventDefault();
            }

            return true;
        }

        return false;
    };

    const handleMobileMegaTab = (event) => {
        if (event.key !== 'Tab' || !isMobileMegaPanelOpen()) {
            return false;
        }

        const focusableElements = [activeTrigger, ...getFocusableElements(activePanel)].filter(Boolean);
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (!firstElement || !lastElement) {
            return false;
        }

        if (event.shiftKey && document.activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
            return true;
        }

        if (!event.shiftKey && document.activeElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
            return true;
        }

        return false;
    };

    const updatePointerStartedInsideMenu = (event) => {
        window.clearTimeout(pointerInsideMenuTimer);
        pointerStartedInsideMenu = menu.contains(event.target);

        if (pointerStartedInsideMenu) {
            pointerInsideMenuTimer = window.setTimeout(() => {
                pointerStartedInsideMenu = false;
            }, 350);
        }
    };

    const handleViewportResize = () => {
        if (isMobileMegaPanelOpen()) {
            updateMobileMegaLayout();
        }
    };

    triggers.forEach((trigger) => {
        trigger.addEventListener('click', () => {
            const panel = menu.querySelector(`#${trigger.getAttribute('aria-controls')}`);
            const isActivePanel = activeTrigger === trigger && activePanel === panel;

            if (!panel) {
                closePanels(false);
                return;
            }

            if (isActivePanel && activeOpenSource === 'click') {
                closePanels(true);
                return;
            }

            if (isActivePanel) {
                keepPanelOpen(trigger, panel, 'click');
                return;
            }

            openPanel(trigger, panel, 'click');
        });
    });

    accordionTriggers.forEach((trigger) => {
        trigger.addEventListener('click', () => {
            if (!megaAccordionMediaQuery.matches) {
                return;
            }

            const panel = menu.querySelector(`#${trigger.getAttribute('aria-controls')}`);

            if (!panel) {
                return;
            }

            if (trigger.getAttribute('aria-expanded') === 'true') {
                closeAccordion(trigger, panel);
                return;
            }

            openAccordion(trigger, panel);
        });
    });

    searchSuggestionTriggers.forEach((trigger) => {
        trigger.addEventListener('click', () => {
            const form = trigger.form;
            const searchInput = form ? form.querySelector('.updatedMainMenu-searchInput') : null;

            if (!form || !searchInput) {
                return;
            }

            searchInput.value = trigger.value;

            if (typeof form.requestSubmit === 'function') {
                form.requestSubmit();
                return;
            }

            form.submit();
        });
    });

    if (backdrop) {
        backdrop.addEventListener('click', () => {
            closePanels(false);
        });
    }

    const handleMegaAccordionMediaQueryChange = () => {
        setMegaAccordionMode();
        syncMobileMegaState();
    };

    if (typeof megaAccordionMediaQuery.addEventListener === 'function') {
        megaAccordionMediaQuery.addEventListener('change', handleMegaAccordionMediaQueryChange);
    } else if (typeof megaAccordionMediaQuery.addListener === 'function') {
        megaAccordionMediaQuery.addListener(handleMegaAccordionMediaQueryChange);
    }

    if (typeof primaryNavHiddenMediaQuery.addEventListener === 'function') {
        primaryNavHiddenMediaQuery.addEventListener('change', closePrimaryNavPanelWhenHidden);
    } else if (typeof primaryNavHiddenMediaQuery.addListener === 'function') {
        primaryNavHiddenMediaQuery.addListener(closePrimaryNavPanelWhenHidden);
    }

    setMegaAccordionMode();
    closePrimaryNavPanelWhenHidden();

    if (canHover) {
        const scheduleHoverClose = () => {
            window.clearTimeout(hoverCloseTimer);

            hoverCloseTimer = window.setTimeout(() => {
                if (
                    activeOpenSource !== 'hover' ||
                    (activeTrigger && activeTrigger.matches(':hover')) ||
                    (activePanel && activePanel.matches(':hover'))
                ) {
                    return;
                }

                closePanels(false);
            }, 120);
        };

        hoverTriggers.forEach((trigger) => {
            trigger.addEventListener('mouseenter', () => {
                window.clearTimeout(hoverOpenTimer);
                window.clearTimeout(hoverCloseTimer);

                const panel = menu.querySelector(`#${trigger.getAttribute('aria-controls')}`);

                if (!panel || trigger.getAttribute('aria-expanded') === 'true') {
                    return;
                }

                if (activeOpenSource === 'hover' && activePanel) {
                    openPanel(trigger, panel, 'hover');
                    return;
                }

                hoverOpenTimer = window.setTimeout(() => {
                    if (trigger.matches(':hover')) {
                        openPanel(trigger, panel, 'hover');
                    }
                }, hoverOpenDelay);
            });

            trigger.addEventListener('mouseleave', () => {
                window.clearTimeout(hoverOpenTimer);
                scheduleHoverClose();
            });
        });

        panels.forEach((panel) => {
            panel.addEventListener('mouseenter', () => {
                window.clearTimeout(hoverCloseTimer);
            });

            panel.addEventListener('mouseleave', scheduleHoverClose);
        });

        menu.addEventListener('mouseleave', () => {
            window.clearTimeout(hoverCloseTimer);
            hoverCloseTimer = window.setTimeout(() => {
                if (activeOpenSource === 'hover') {
                    closePanels(false);
                }
            }, 120);
        });
    }

    document.addEventListener('pointerdown', updatePointerStartedInsideMenu);
    window.addEventListener('resize', handleViewportResize);

    document.addEventListener('click', (event) => {
        if (!menu.contains(event.target)) {
            closePanels(false);
        }
    });

    menu.addEventListener('focusout', () => {
        window.requestAnimationFrame(() => {
            if (!activePanel || pointerStartedInsideMenu || menu.contains(document.activeElement)) {
                return;
            }

            closePanels(false);
        });
    });

    menu.addEventListener('keydown', (event) => {
        if (handleMobileMegaTab(event)) {
            return;
        }

        if (handleHeaderDropdownTab(event)) {
            return;
        }

        if (event.key === 'Escape' || event.key === 'Esc') {
            event.preventDefault();
            closePanels(true);
        }
    });
});
